import { STONES_PER_ETAPE } from '@iqrapath/shared'
import { useEffect, useRef, useState, type CSSProperties } from 'react'
import { Island, MAIN_LAMP } from '../home/Island.tsx'
import { SkyBackdrop } from '../home/SkyBackdrop.tsx'
import type { CompleteResponse } from './ayat.ts'

/** 18 sparks around the main lamp, deterministic so the layout never jumps. */
const SPARKS = Array.from({ length: 18 }, (_, i) => {
  const angle = (i / 18) * Math.PI * 2
  const dist = 46 + (i % 3) * 22
  return { dx: Math.round(Math.cos(angle) * dist), dy: Math.round(Math.sin(angle) * dist), delay: (i % 6) * 40 }
})

const reducedMotion = () => typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches

/**
 * "Lampu menyala" (docs/UX-PLAYBOOK.md §8): dark → lamp lights at 1 s (short vibration) → bloom + sparks → text at 2.5 s.
 * Skippable at any time; with reduced motion the final state shows at once.
 */
export function Celebration({ result, onClose }: { result: CompleteResponse; onClose: () => void }) {
  const [stage, setStage] = useState<0 | 1 | 2>(() => (reducedMotion() ? 2 : 0))
  const primary = useRef<HTMLButtonElement>(null)
  const skip = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (stage === 2) return
    skip.current?.focus()
    const t1 = setTimeout(() => { setStage(1); navigator.vibrate?.(18) }, 1000)
    const t2 = setTimeout(() => setStage(2), 2500)
    return () => { clearTimeout(t1); clearTimeout(t2) }
  }, []) // once: the timeline must not restart when the stage changes

  useEffect(() => { if (stage === 2) primary.current?.focus() }, [stage])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') (stage === 2 ? onClose() : setStage(2)) }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [stage, onClose])

  const { stats, firstTime } = result
  const sub = !firstTime
    ? 'Ayat ini sudah tercatat sebelumnya.'
    : stats.stones === STONES_PER_ETAPE
      ? `Batu ke-${STONES_PER_ETAPE} terpasang, etape ke-${stats.etape} lengkap.`
      : `Satu batu pijakan bertambah${stats.streak > 1 ? `, ${stats.streak} hari berturut-turut` : ''}.`

  return (
    <div className="celebrate" role="dialog" aria-modal="true" aria-labelledby="cel-t" data-stage={stage}>
      <SkyBackdrop phase="isya" />
      <div className="cel-stage">
        <Island phase="isya" lit={stage >= 1} />
        {stage >= 1 && !reducedMotion() && (
          <div className="sparks" aria-hidden="true" style={{ left: `${MAIN_LAMP.x}%`, top: `${MAIN_LAMP.y}%` }}>
            <i className="bloom" />
            {SPARKS.map((s, i) => <i key={i} className="spark" style={{ '--dx': `${s.dx}px`, '--dy': `${s.dy}px`, animationDelay: `${s.delay}ms` } as CSSProperties} />)}
          </div>
        )}
      </div>
      <div className={`cel-text${stage === 2 ? ' show' : ''}`}>
        <h2 id="cel-t">Setapak hari ini sudah terang</h2>
        <p>{sub}</p>
        <p className="cel-note">Semoga ayat hari ini menemanimu sampai esok.</p>
        {stage === 2 && <button ref={primary} type="button" className="btn btn-primary" onClick={onClose}>Kembali ke Beranda</button>}
      </div>
      {stage < 2 && <button ref={skip} type="button" className="cel-skip" onClick={() => setStage(2)}>Lewati</button>}
    </div>
  )
}
